import { useEffect } from 'react'
import { Head, usePage } from '@inertiajs/react'
import { usePublicPage } from '@/hooks/usePublicPage'
import { LandingNavbar } from '@/components/landing/LandingNavbar'
import { LandingHero } from '@/components/landing/LandingHero'
import { ExamCategoriesSection, ExamSummary } from '@/components/landing/ExamCategoriesSection'
import { PremiumFeaturesSection } from '@/components/landing/PremiumFeaturesSection'
import { MissionPricingSection } from '@/components/landing/MissionPricingSection'
import { LandingFooter } from '@/components/landing/LandingFooter'
import type { PageProps } from '@/types'

interface HomeIndexProps {
  exams?: ExamSummary[]
}

export default function HomeIndex({ exams = [] }: HomeIndexProps) {
  const { auth } = usePage<PageProps>().props
  usePublicPage()

  useEffect(() => {
    document.title = 'PrepKiya — Master Competitive Exams with Confidence'
  }, [])

  const jsonLdWebsite = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'PrepKiya',
    url: 'https://prepkiya.com',
    description: "India's trusted platform for Indian competitive exams preparation, authentic timed mock tests, and 4-method solutions.",
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://prepkiya.com/exams?q={search_term_string}',
      'query-input': 'required name=search_term_string'
    }
  }

  const jsonLdOrg = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: 'PrepKiya',
    url: 'https://prepkiya.com',
    logo: 'https://prepkiya.com/landing-logo.png',
    sameAs: []
  }

  return (
    <>
      <Head title="Free Mock Tests & Indian Competitive Exam Preparation | PrepKiya">
        <meta
          name="description"
          content="Prepare for Indian competitive exams with authentic timed mock tests for GATE, Bank PO, SBI Clerk, and NISM. 4-method solutions, percentile ranking & instant diagnostics."
        />
        <meta
          name="keywords"
          content="mock test, competitive exams, GATE preparation, Indian exams, SBI clerk mock test, bank exam practice, NISM test series, free mock test online"
        />
        <link rel="canonical" href="https://prepkiya.com/" />
        <meta property="og:title" content="Free Mock Tests & Indian Competitive Exam Preparation | PrepKiya" />
        <meta
          property="og:description"
          content="Practice with high-yield mock tests, analyze performance, and learn step-by-step solutions for GATE, Banking, and NISM certifications."
        />
        <meta property="og:url" content="https://prepkiya.com/" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdWebsite) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdOrg) }}
        />
      </Head>

      <div className="min-h-screen w-full flex flex-col bg-white text-slate-900 antialiased selection:bg-[#c8933b]/20 selection:text-slate-900">
        {/* Navigation Bar */}
        <LandingNavbar user={auth?.user} />

        {/* Main Content Sections */}
        <main className="w-full flex-1 flex flex-col">
          {/* Hero Section */}
          <LandingHero user={auth?.user} />

          {/* Explore Exam Categories Grid */}
          <ExamCategoriesSection exams={exams} user={auth?.user} />

          {/* Premium Features: 4 Solution Methods & Topper High-Yield Notes */}
          <PremiumFeaturesSection />

          {/* Mission Statement & Pay-Per-Test Pricing */}
          <MissionPricingSection />
        </main>

        {/* Footer */}
        <LandingFooter />
      </div>
    </>
  )
}
